/**
 * Agent-Chat Sidebar Component
 * Host-first tree: Host → Mission → Execution
 */

import React from 'react'
import { Link, useNavigate } from 'react-router'
import { ChevronLeft, Search, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useChatSidebarModel } from '../../hooks/useChatSidebarModel'
import {
  Avatar,
  Button,
  SearchInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RoleBadge,
  Text,
  cn,
  type SystemRole,
} from '@agion/ui'
import { getInitials } from '../../utils/stringHelpers'
import { config } from '../../utils/config'
import AgionLogo from '../../assets/agion-logo.svg'
import AgionLogoDark from '../../assets/agion-logo-dark.svg'
import { useThemeContext } from '../../providers/ThemeProvider'
import { NewMissionChatDialog } from '../chat/NewMissionChatDialog'
import { AttentionSection } from '../sidebar/OpenSection'
import { EscalationsSection } from '../sidebar/EscalationsSection'
import { RecentSection } from '../sidebar/RecentSection'
import { MissionSkeleton } from './Skeletons'
import { AppSwitcher } from './AppSwitcher'
import { OrgSwitcher } from './OrgSwitcher'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const { t } = useTranslation(['common', 'chat', 'errors'])
  const navigate = useNavigate()
  const { resolvedTheme } = useThemeContext()
  // The data lives in useChatSidebarModel, shared with the new chat shell
  // (PLAN-20260929 slice 6); this component renders it unchanged.
  const {
    userProfile,
    handleLogout,
    searchTerm,
    setSearchTerm,
    hostGroups,
    allMissionsFlat,
    urlMissionId,
    isLoading,
    hitlRequests,
    governanceAlerts,
  } = useChatSidebarModel()
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const handleGoToAdmin = () => {
    window.location.href = config.adminUrl
  }

  const [showNewMissionDialog, setShowNewMissionDialog] = React.useState(false)

  return (
    <div className="h-full flex flex-col">
      {/* Logo + Collapse Toggle */}
      <div className="group flex items-center justify-between h-16 pl-4 pr-3">
        <div className="flex items-center gap-2.5">
          {!collapsed && (
            <Button
              variant="ghost"
              onClick={() => navigate('/chat')}
              className="p-0 h-auto hover:bg-transparent hover:opacity-80 transition-opacity"
              aria-label="Go to home"
            >
              <img
                src={resolvedTheme === 'dark' ? AgionLogoDark : AgionLogo}
                alt="Agion"
                className="h-6 w-auto"
              />
            </Button>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
        </Button>
      </div>

      {/* New Chat Button - Brand colored, prominent */}
      {!collapsed && (
        <div className="px-3 mb-3">
          <Button
            size="sm"
            className="w-full gap-2 bg-amber-500 hover:bg-amber-600 text-white dark:bg-amber-600 dark:hover:bg-amber-500 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
            onClick={() => navigate('/chat')}
          >
            <Plus className="h-4 w-4" />
            {t('chat:newChat', 'New Chat')}
          </Button>
        </div>
      )}
      {collapsed && (
        <div className="px-2 mb-3">
          <Button
            size="icon"
            onClick={() => navigate('/chat')}
            title={t('chat:newChat', 'New Chat')}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white dark:bg-amber-600 dark:hover:bg-amber-500 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Search */}
      {!collapsed ? (
        <div className="px-3 mb-3">
          <SearchInput
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchTerm('')
                searchInputRef.current?.blur()
              }
            }}
            onClear={() => setSearchTerm('')}
            placeholder={t('chat:searchMissions', 'Search missions...')}
            size="sm"
            className="bg-white dark:bg-slate-800 h-9"
          />
        </div>
      ) : null}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto px-3 pb-4 sidebar-scroll">
        {/* Intent-based sections (expanded sidebar only) */}
        {!collapsed && (
          <>
            {/* Escalations — persistent governance approvals (REST-backed) */}
            <EscalationsSection searchTerm={searchTerm} />

            {/* Needs Your Response — HITL requests + governance alerts */}
            <AttentionSection searchTerm={searchTerm} />

            {/* Recent — recently active missions (includes running) */}
            <RecentSection searchTerm={searchTerm} />

            {/* ALL MISSIONS — flat alphabetical list */}
            <div className="px-1 pt-3 pb-1.5">
              <Text size="xs" weight="semibold" className="uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {t('chat:allMissions', 'All Missions')}
              </Text>
            </div>

            <div>
                {isLoading ? (
                  <div className="px-3 py-2" data-testid="conversation-skeleton">
                    <MissionSkeleton />
                  </div>
                ) : allMissionsFlat.length === 0 ? (
                  <div className="px-3 py-4 text-center">
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      {searchTerm
                        ? t('chat:noMatchingMissions', 'No matching missions')
                        : t('chat:noMissions', 'No missions available')
                      }
                    </p>
                  </div>
                ) : (
                  allMissionsFlat.map((mission) => {
                    const isActive = urlMissionId === mission.missionId
                    return (
                      <Button
                        key={mission.missionId}
                        variant="ghost"
                        onClick={() => navigate(`/mission/${mission.missionId}`)}
                        className={cn(
                          'w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm h-auto text-left',
                          isActive
                            ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30'
                        )}
                      >
                        <span className="truncate flex-1">{mission.missionName}</span>
                        {mission.isRunning && (
                          <span className="relative flex h-2 w-2 flex-shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                          </span>
                        )}
                      </Button>
                    )
                  })
                )}
              </div>
          </>
        )}

        {/* Collapsed: single popover with flat mission list */}
        {collapsed && (
          /* Collapsed: single popover with host→mission compact tree */
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 relative rounded-lg hover:bg-white/80 dark:hover:bg-slate-800/50 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
                title={t('chat:missions', 'Missions')}
              >
                <Search className="h-5 w-5 flex-shrink-0" />
                {(hitlRequests.size > 0 || governanceAlerts.size > 0) && (
                  <span className={cn(
                    'absolute top-1.5 right-1.5 w-2 h-2 rounded-full animate-pulse',
                    governanceAlerts.size > 0 ? 'bg-rose-500' : 'bg-amber-500'
                  )} />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent side="right" align="start" sideOffset={8} className="w-56 p-1.5 max-h-80 overflow-y-auto sidebar-scroll">
              {/* Flat mission list: governance first, HITL second, running third, rest alphabetical */}
              {hostGroups.flatMap(g => g.missions)
                .sort((a, b) => {
                  if (a.hasGovernanceAlert !== b.hasGovernanceAlert) return a.hasGovernanceAlert ? -1 : 1
                  if (a.hasActiveHITL !== b.hasActiveHITL) return a.hasActiveHITL ? -1 : 1
                  if (a.isRunning !== b.isRunning) return a.isRunning ? -1 : 1
                  return a.missionName.localeCompare(b.missionName)
                })
                .map((mission) => (
                  <Button
                    key={mission.missionId}
                    variant="ghost"
                    onClick={() => navigate(`/mission/${mission.missionId}`)}
                    className={cn(
                      'w-full flex items-center justify-start gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors text-left h-auto',
                      'focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1',
                      mission.isDraft && 'opacity-60',
                      'text-slate-700 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-800/50'
                    )}
                  >
                    <span
                      className={cn(
                        'w-1.5 h-1.5 rounded-full flex-shrink-0',
                        mission.hasGovernanceAlert ? 'bg-rose-500 animate-pulse'
                          : mission.hasActiveHITL ? 'bg-amber-500 animate-pulse'
                          : mission.isRunning ? 'bg-emerald-500 animate-pulse'
                          : 'bg-slate-300 dark:bg-slate-600'
                      )}
                    />
                    <span className="truncate">{mission.missionName}</span>
                  </Button>
                ))
              }
              {hostGroups.length === 0 && (
                <p className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">
                  {t('chat:noMissions', 'No missions available')}
                </p>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Footer - Org + Apps + User */}
      <div className="border-t border-slate-200 dark:border-slate-800 p-2 space-y-1">
        <OrgSwitcher
          collapsed={collapsed}
          currentOrgId={userProfile?.organizationId ?? userProfile?.organization_id}
        />
        <AppSwitcher collapsed={collapsed} />

        {/* Language switcher lives on /profile — matches admin-ui (F24). */}

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className={cn(
              "flex items-center gap-2.5 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer",
              collapsed && "justify-center px-0"
            )}>
              <Avatar
                fallback={getInitials(userProfile?.name || userProfile?.email)}
                size="sm"
                className="flex-shrink-0"
              />
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Text weight="medium" className="truncate">
                      {userProfile?.name || userProfile?.email || t('common:user')}
                    </Text>
                    {userProfile?.role && (
                      <RoleBadge role={userProfile.role as SystemRole} size="sm" />
                    )}
                  </div>
                  <Text size="xs" color="muted" className="truncate">
                    {userProfile?.organizationName || t('chat:freePlan', 'Free plan')}
                  </Text>
                </div>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="end"
            className="w-56"
          >
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700/50">
              <div className="flex items-center gap-3">
                <Avatar
                  fallback={getInitials(userProfile?.name || userProfile?.email)}
                  size="md"
                  className="flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <Text weight="medium" className="truncate">
                    {userProfile?.name || userProfile?.email || t('common:user')}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Text size="xs" color="muted" className="truncate">
                      {userProfile?.organizationName || t('chat:freePlan', 'Free plan')}
                    </Text>
                    {userProfile?.role && (
                      <RoleBadge role={userProfile.role as SystemRole} size="sm" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="py-1">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-3">
                  <span>{t('common:profileSettings')}</span>
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={handleGoToAdmin}
                className="flex items-center gap-3 cursor-pointer"
              >
                <span>{t('common:adminDashboard')}</span>
              </DropdownMenuItem>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700/50 pt-1 pb-1">
              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center gap-3 cursor-pointer"
              >
                <span>{t('common:logout')}</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* New Mission Dialog */}
      <NewMissionChatDialog
        isOpen={showNewMissionDialog}
        onClose={() => setShowNewMissionDialog(false)}
      />
    </div>
  )
}

export default Sidebar
